import { type FormEvent, useId, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import {
  getGetMyReminderPreferencesQueryKey,
  getGetReminderPolicyQueryKey,
  useGetMyReminderPreferences,
  useGetReminderPolicy,
  usePutMyReminderPreferences,
  usePutReminderPolicy,
} from '../../api/generated/notification/notification';
import type { ReminderPolicy, ReminderPreferences as Preferences } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { formatNumber } from '../../i18n/datetime';
import styles from './settings.module.css';

/** A faixa que o contrato aceita para a antecedencia, nos dois formularios. */
const MIN_MINUTES = 5;
const MAX_MINUTES = 1440;

/**
 * Antecedencia digitada, e nao numero.
 *
 * O campo guarda texto porque apagar precisa significar VAZIO. Com estado
 * numerico e `Number(event.target.value)`, apagar o campo para trocar 60 por 90
 * — que e como quase todo mundo faz — transformava o valor em 0 no mesmo
 * instante: um estado que ninguem pediu, recusado depois pelo navegador, com
 * bolha no idioma do sistema operacional em vez do idioma escolhido na
 * plataforma. Aqui a faixa e conferida em portugues, ingles ou espanhol, pelo
 * mesmo catalogo do resto da tela.
 */
function minutesOf(typed: string): number | null {
  const trimmed = typed.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value >= MIN_MINUTES && value <= MAX_MINUTES ? value : null;
}

/**
 * Preferencias de lembrete de aula.
 *
 * A antecedencia default e de 60 minutos e o valor e **provisorio**, registrado
 * no roadmap para ratificacao — e a tela diz isso, em vez de apresentar um
 * numero pendente como se fosse regra fechada.
 *
 * A politica da escola e leitura exclusiva de Super Admin no contrato, entao
 * ela so aparece para quem pode le-la; para os demais papeis a tela nao inventa
 * o valor nem tenta a rota que responderia 403.
 */
export function ReminderPreferences() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const preferencesQuery = useGetMyReminderPreferences({ query: { enabled: !!me } });
  const policyQuery = useGetReminderPolicy({
    query: { enabled: me?.role === 'super_admin' },
  });

  if (!me) return null;

  const preferences = preferencesQuery.data;
  const policy = policyQuery.data;

  return (
    <div className={styles.page}>
      <h1>{t('reminders.title')}</h1>

      {/*
        Pendencia de valor nao segura formato: o default existe, funciona e esta
        escrito na tela como provisorio ate a ratificacao.
      */}
      <Alert tone="warning">{t('reminders.provisional')}</Alert>

      {policy ? (
        <section aria-labelledby="reminder-policy">
          <h2 id="reminder-policy">{t('reminders.policyTitle')}</h2>
          <p>
            {t('reminders.policyDefault', {
              minutes: formatNumber(policy.defaultMinutesBefore, me.locale),
            })}
          </p>
          <p>{policy.allowOptOut ? t('reminders.optOutAllowed') : t('reminders.optOutBlocked')}</p>

          {/*
            A politica governa a plataforma inteira — o default de antecedencia
            e se a pessoa pode desligar o lembrete — e ate aqui era so leitura
            na interface: `putReminderPolicy` existe no contrato e nao tinha
            tela nenhuma. A propria pagina avisa que os 60 minutos sao
            provisorios e aguardam ratificacao; sem este formulario, no dia da
            ratificacao nao haveria por onde aplica-la.

            Fica dentro do bloco `policy`, que ja e exclusivo de Super Admin: o
            contrato reserva a leitura a esse papel, e desenhar o formulario
            para quem levaria 403 seria pedir decisao para jogar fora.
          */}
          <PolicyForm initial={policy} locale={me.locale} />
        </section>
      ) : null}

      {preferencesQuery.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {preferences ? <PreferencesForm initial={preferences} policy={policy} locale={me.locale} /> : null}
    </div>
  );
}

function PreferencesForm({
  initial,
  policy,
  locale,
}: {
  readonly initial: Preferences;
  readonly policy: ReminderPolicy | undefined;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const minutesId = useId();
  const save = usePutMyReminderPreferences();

  // Estado inicial capturado na montagem, que so acontece depois de a leitura
  // chegar. Semear estado dentro de um efeito produziria um render a mais e
  // sobrescreveria o que a pessoa ja tivesse digitado.
  const [form, setForm] = useState<Preferences>(initial);
  const [minutes, setMinutes] = useState(String(initial.classReminderMinutesBefore));
  const [saved, setSaved] = useState(false);

  const optOutBlocked = policy?.allowOptOut === false;
  const parsedMinutes = minutesOf(minutes);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (parsedMinutes === null) return;
    setSaved(false);
    save.mutate(
      { data: { ...form, classReminderMinutesBefore: parsedMinutes } },
      {
        onSuccess: () => {
          setSaved(true);
          void queryClient.invalidateQueries({
            queryKey: getGetMyReminderPreferencesQueryKey(),
          });
        },
      },
    );
  };

  return (
    // `noValidate`: quem recusa a faixa e esta tela, no idioma escolhido na
    // plataforma, e nao o navegador no idioma do sistema operacional.
    <form className={styles.form} onSubmit={submit} noValidate>
      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={form.classReminderEnabled}
          disabled={optOutBlocked}
          onChange={(event) =>
            setForm((current) => ({ ...current, classReminderEnabled: event.target.checked }))
          }
        />
        <span>{t('reminders.classReminder')}</span>
      </label>
      {optOutBlocked ? <p className={styles.hint}>{t('reminders.optOutBlocked')}</p> : null}

      <label className={styles.label} htmlFor={minutesId}>
        {t('reminders.minutesBefore')}
      </label>
      <input
        id={minutesId}
        className={styles.input}
        type="number"
        min={MIN_MINUTES}
        max={MAX_MINUTES}
        value={minutes}
        aria-invalid={parsedMinutes === null ? true : undefined}
        onChange={(event) => setMinutes(event.target.value)}
      />
      <p className={styles.hint}>
        {t('reminders.minutesRange', {
          min: formatNumber(MIN_MINUTES, locale),
          max: formatNumber(MAX_MINUTES, locale),
        })}
      </p>
      {parsedMinutes === null ? (
        <Alert tone="error">
          {t('reminders.minutesInvalid', {
            min: formatNumber(MIN_MINUTES, locale),
            max: formatNumber(MAX_MINUTES, locale),
          })}
        </Alert>
      ) : null}

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={form.emailEnabled}
          disabled={optOutBlocked}
          onChange={(event) =>
            setForm((current) => ({ ...current, emailEnabled: event.target.checked }))
          }
        />
        <span>{t('reminders.email')}</span>
      </label>

      <Button
        type="submit"
        variant="primary"
        loading={save.isPending}
        disabled={parsedMinutes === null}
      >
        {t('reminders.save')}
      </Button>

      {saved ? <Alert tone="success">{t('reminders.saved')}</Alert> : null}
      {save.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
    </form>
  );
}

/**
 * A politica da escola, editavel.
 *
 * `putReminderPolicy` e normativa no contrato e nao tinha tela nenhuma: a
 * antecedencia default de toda a plataforma e a permissao de desligar o
 * lembrete eram imutaveis pela interface, e a propria pagina anuncia que os 60
 * minutos sao provisorios ate ratificacao. Ratificar sem lugar onde aplicar
 * seria decidir e nao poder executar.
 *
 * O formulario e o mesmo desenho do de preferencias logo acima — inclusive a
 * antecedencia como texto, pelo mesmo motivo — porque sao a mesma pergunta
 * feita em dois escopos: um vale para a escola, outro para a pessoa.
 */
function PolicyForm({
  initial,
  locale,
}: {
  readonly initial: ReminderPolicy;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const minutesId = useId();
  const save = usePutReminderPolicy();

  const [minutes, setMinutes] = useState(String(initial.defaultMinutesBefore));
  const [allowOptOut, setAllowOptOut] = useState(initial.allowOptOut);
  const [saved, setSaved] = useState(false);

  const parsedMinutes = minutesOf(minutes);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (parsedMinutes === null) return;
    setSaved(false);
    save.mutate(
      { data: { defaultMinutesBefore: parsedMinutes, allowOptOut } },
      {
        onSuccess: () => {
          setSaved(true);
          // A politica governa a preferencia de todo mundo — `allowOptOut`
          // desabilita o controle da pessoa —, entao as DUAS leituras ficam
          // velhas quando ela muda.
          void queryClient.invalidateQueries({ queryKey: getGetReminderPolicyQueryKey() });
          void queryClient.invalidateQueries({
            queryKey: getGetMyReminderPreferencesQueryKey(),
          });
        },
      },
    );
  };

  return (
    <form className={styles.form} onSubmit={submit} aria-label={t('reminders.policyEdit')} noValidate>
      <h3>{t('reminders.policyEdit')}</h3>

      <label className={styles.label} htmlFor={minutesId}>
        {t('reminders.policyMinutes')}
      </label>
      <input
        id={minutesId}
        className={styles.input}
        type="number"
        min={MIN_MINUTES}
        max={MAX_MINUTES}
        value={minutes}
        aria-invalid={parsedMinutes === null ? true : undefined}
        onChange={(event) => setMinutes(event.target.value)}
      />
      <p className={styles.hint}>
        {t('reminders.minutesRange', {
          min: formatNumber(MIN_MINUTES, locale),
          max: formatNumber(MAX_MINUTES, locale),
        })}
      </p>
      {parsedMinutes === null ? (
        <Alert tone="error">
          {t('reminders.minutesInvalid', {
            min: formatNumber(MIN_MINUTES, locale),
            max: formatNumber(MAX_MINUTES, locale),
          })}
        </Alert>
      ) : null}

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={allowOptOut}
          onChange={(event) => setAllowOptOut(event.target.checked)}
        />
        <span>{t('reminders.policyOptOut')}</span>
      </label>

      <Button
        type="submit"
        variant="primary"
        loading={save.isPending}
        disabled={parsedMinutes === null}
      >
        {t('reminders.policySave')}
      </Button>

      {saved ? <Alert tone="success">{t('reminders.policySaved')}</Alert> : null}
      {save.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
    </form>
  );
}
